import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { ROUTING_I18N } from "./utils/campaignRoutingFields";
import { fallbackOutcomeOf, FallbackOutcome } from "./utils/campaignRoutingFallback";
import { RoutingTranslate } from "./utils/campaignRoutingText";
import { RoutingCampaignConfig, RoutingCampaignStatuses } from "./utils/campaignRoutingTargets";
import { useCampaignRoutingEditor } from "./useCampaignRoutingEditor";

interface AutoAssignRoutingLineProps {
	listId: number;
	campaignConfig: RoutingCampaignConfig;
	campaignStatuses: RoutingCampaignStatuses;
	autoAssignEnabled: boolean;
}

const fallbackText = (outcome: FallbackOutcome, t: RoutingTranslate): string => {
	switch (outcome.kind) {
		case "DEFAULT":
			return t(`${ROUTING_I18N}.settingsLine.fallbackDefault`, { campaign: outcome.campaignName });
		case "DEFAULT_UNUSABLE":
		case "NO_DEFAULT":
			return t(`${ROUTING_I18N}.settingsLine.fallbackNoDefault`);
		case "NONE":
			return t(`${ROUTING_I18N}.settingsLine.fallbackNone`);
	}
};

/**
 * List settings → Advanced settings, under "Minimum score" (PREC-4219, mockup frame 4): where
 * auto-assigned leads go — the first matching routing rule, else the list's fallback — and a link
 * into the same routing editor the Campaigns section opens.
 */
export const AutoAssignRoutingLine: FunctionComponent<AutoAssignRoutingLineProps> = ({
	listId,
	campaignConfig,
	campaignStatuses,
	autoAssignEnabled,
}) => {
	const { t } = useTranslation();
	const routing = useCampaignRoutingEditor({
		listId,
		campaignConfig,
		campaignStatuses,
		autoAssignEnabled,
	});

	if (routing.failed) {
		return (
			<p className="mt-2 text-xs text-status-error" data-testid="auto-assign-routing-line">
				{t(`${ROUTING_I18N}.settingsLine.loadFailed`)}
			</p>
		);
	}

	if (!routing.settings) {
		return null;
	}

	const fallback = fallbackText(fallbackOutcomeOf(routing.settings, routing.campaigns), t);
	const count = routing.settings.rules.length;

	return (
		<>
			<p className="mt-2 text-xs text-text-meta" data-testid="auto-assign-routing-line">
				{count > 0
					? t(`${ROUTING_I18N}.settingsLine.withRules`, { count, fallback })
					: t(`${ROUTING_I18N}.settingsLine.noRules`, { fallback })}{" "}
				<button
					className="cursor-pointer font-medium text-blue-action underline underline-offset-2"
					data-testid="auto-assign-routing-edit"
					type="button"
					onClick={routing.open}
				>
					{t(`${ROUTING_I18N}.settingsLine.editLink`)}
				</button>
			</p>
			{routing.editor}
		</>
	);
};
